import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Popover,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { mdiLink } from '@mdi/js';
import Icon from '@mdi/react';
import PopupTitle from '../common/popup_title';
import { formatDateInLocale } from '../../services/dates';
import {
  useCreateSharedBoardMutation,
  useCreateSharedMealMutation,
  useDeleteSharedMealMutation,
  useGetSmorgBoardQuery,
  useLazyGetDistilledSharedMealsByParentQuery,
  useLazyGetFullMealsByParentQuery,
  useLazyGetSharedBoardQuery,
  useUpdateSharedBoardMutation,
  useUpdateSmorgBoardMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { userCountryCode } from '../../services/users';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { sharedBoardIDFromBoard } from '../../services/smorg_board';
import { useTracker } from '../../operations/tracking_operations';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  addStandaloneRecipesBoardShareRecord,
  createSharedBoardFromRecipesBoard,
  setSharedBoardAccessLevel,
} from '../../operations/recipes_operations';

const SmorgBoardShareModal = ({
  recipesBoardId,
  recipesBoardAndMealsLastUpdatedAt,
  anchorEl,
}) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { data: recipesBoard } = useGetSmorgBoardQuery(recipesBoardId, {
    skip: !recipesBoardId,
  });

  const sharedBoardID = sharedBoardIDFromBoard(recipesBoard);

  const { data: userProfile } = useGetUserProfileQuery();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const [lazyGetDistilledSharedMealsByParent] =
    useLazyGetDistilledSharedMealsByParentQuery();

  const [createSharedBoard] = useCreateSharedBoardMutation();

  const [updateSharedBoard] = useUpdateSharedBoardMutation();

  const [createSharedMeal] = useCreateSharedMealMutation();

  const [deleteSharedMeal] = useDeleteSharedMealMutation();

  const tracker = useTracker();

  const countryCode = userCountryCode(userProfile);

  const [sharedBoard, setSharedBoard] = useState(null);
  const [loading, setLoading] = useState(false);

  const isPublic = sharedBoard?.accessLevel === 'PUBLIC';
  const sharedBoardNeedsUpdate =
    sharedBoard &&
    (!recipesBoardAndMealsLastUpdatedAt ||
      sharedBoard.updatedAt < recipesBoardAndMealsLastUpdatedAt);

  const [getSharedBoard] = useLazyGetSharedBoardQuery();

  useEffect(() => {
    if (!sharedBoardID) {
      return;
    }
    setLoading(true);
    getSharedBoard(sharedBoardID, true).then((b) => {
      setSharedBoard(b);
      setLoading(false);
    });
  }, [getSharedBoard, sharedBoardID]);

  const setPublic = async (shouldBePublic) => {
    try {
      setLoading(true);
      if (shouldBePublic) {
        tracker(userProfile, ['Board made public'], ['numRecipesBoardsShared']);
        if (sharedBoard) {
          const sb = await setSharedBoardAccessLevel(
            sharedBoard,
            'PUBLIC',
            updateSharedBoard,
          );
          // TODO copy latest updates
          setSharedBoard(sb);
          await addStandaloneRecipesBoardShareRecord(
            recipesBoard,
            sb.id,
            '1',
            sb.updatedAt,
            updateSmorgBoard,
          );
        } else {
          const sb = await createSharedBoardFromRecipesBoard(
            recipesBoard,
            sharedBoard.id,
            lazyGetFullMealsByParent,
            lazyGetDistilledSharedMealsByParent,
            createSharedBoard,
            createSharedMeal,
            deleteSharedMeal,
            updateSmorgBoard,
            updateSharedBoard,
          );
          setSharedBoard(sb);
        }
      } else {
        tracker(userProfile, ['Board made private']);
        const sb = await setSharedBoardAccessLevel(
          sharedBoard,
          'NONE',
          updateSharedBoard,
        );
        setSharedBoard(sb);
      }
    } finally {
      setLoading(false);
    }
  };

  const onCopyUpdatesToSharedBoard = async () => {
    const sb = await createSharedBoardFromRecipesBoard(
      recipesBoard,
      sharedBoard.id,
      lazyGetFullMealsByParent,
      lazyGetDistilledSharedMealsByParent,
      createSharedBoard,
      createSharedMeal,
      deleteSharedMeal,
      updateSmorgBoard,
      updateSharedBoard,
    );
    setSharedBoard(sb);
  };

  const onCopyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    dispatch(showGlobalSnackbar({ notificationText: 'Link copied' }));
    tracker(userProfile, [{ name: 'Shared board link copied' }]);
    tracker(userProfile, ['Shared board link copied']);
  };

  const onDismiss = () => {
    navigate(-1);
    // navigate(`/recipes/${recipesBoardId}`);
  };

  const urlBase = `${window.location.protocol}//${window.location.host}`;
  const publicUrl = `${urlBase}/board/i/${sharedBoardID}`;

  return (
    <Popover
      open
      PaperProps={{
        style: { width: '260px', height: '200px', padding: '15px' },
      }}
      onClose={onDismiss}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}>
      <Grid container>
        <Grid item xs={12}>
          <PopupTitle
            titleText="Share this board"
            closeButtonEnabled
            onClickClose={onDismiss}
          />
        </Grid>
        <Grid item xs={12} style={{ padding: '5px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isPublic}
                disabled={loading}
                onChange={(ev) => {
                  setPublic(ev.target.checked);
                }}
              />
            }
            label="Make this board public"
          />
        </Grid>
        {recipesBoardAndMealsLastUpdatedAt && (
          <Grid item xs={12} style={{ padding: '5px 5px 15px', fontSize: 12 }}>
            Board last updated{' '}
            {formatDateInLocale(recipesBoardAndMealsLastUpdatedAt, countryCode)}
          </Grid>
        )}
        <Grid item xs={12} style={{ padding: '5px' }}>
          <Grid container>
            <Grid item xs={8}>
              <Button
                onClick={onCopyLink}
                variant="contained"
                size="medium"
                color="secondary"
                disableElevation
                disabled={loading || !isPublic || !sharedBoardID}
                style={{ justifyContent: 'flex-start' }}>
                <Icon
                  path={mdiLink}
                  style={{ width: '24px', height: '24px' }}
                />
                Copy link
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                onClick={(ev) => {
                  ev.stopPropagation();
                  onCopyUpdatesToSharedBoard();
                }}
                variant="contained"
                size="medium"
                color="primary"
                disableElevation
                disabled={!isPublic || !sharedBoardNeedsUpdate}
                style={{ justifyContent: 'flex-start' }}>
                Update
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Popover>
  );
};

SmorgBoardShareModal.propTypes = {
  recipesBoardId: PropTypes.string.isRequired,
  recipesBoardAndMealsLastUpdatedAt: PropTypes.string,
  anchorEl: PropTypes.element.isRequired,
};

SmorgBoardShareModal.defaultProps = {
  recipesBoardAndMealsLastUpdatedAt: null,
};

export default SmorgBoardShareModal;
